import BaseModal from 'components/BaseModal'
import WizardFormV2 from 'components/WizardFormV2'
import { MARGIN_LG, MODAL_SIZE_MEDIUM, BASE_GUTTER } from 'constants/Layout'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import ProductAPI from 'restAPI/ProductAPI';
import UserAPI from 'restAPI/UserAPI';
import SubProductAPI from "restAPI/SubProductAPI";
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import _ from 'lodash';
import { editorRules, FieldTypes, multiUrlValidator, urlRegexValidator } from 'utils/formUtils';
import { checkAndSetConfidentialityData, getDefaultTierValues, getOwnerFields, getSecurityAndComplianceFields, syncTagsFromTier, syncTierFromTags, appendVersionToName } from '../common';
import { Form, Row, Col, Alert, Typography, Select, Tooltip } from 'antd';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import message from 'components/CustomMessage';
import TeamsAPI from 'restAPI/TeamsAPI';
// import IntegrationPopup from './IntegrationPopup';
import { TagsField, ArtifactsField } from '../Product/ProductFormPopup';
import { InfoIcon } from 'components/Icons';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { useFetchProductStatus } from '../Product/ProductStatus'
import MessageEditor from 'components/MessageEditor';
import ToolSelector from './ToolSelector';
import SLAPreview from '../SLAPreview';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext';
import { getKeyValuePair } from 'views/GlobalSettings/TierPresetConfiguration';
import { getGlobalFilter_UNSAFE } from 'contexts/GlobalFilterContext';
import { ORIGIN_OPTIONS, OWNER_FIELDS, REPOSITORY_TYPE_OPTIONS, programmingLanguages, subproductNameFieldRule, updateDataAfterGet, updateDataBeforePush } from './common';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import useTierBasedTags from 'hooks/useTierBasedTags';
import { CUSTOM_TIER, TIER_OPTIONS, TIER_OPTIONS_WITH_CUSTOM } from 'utils/tierUtil';
import TierBasedTagField from '../TierBasedTagField';
import { useGlobalUserListContext } from 'contexts/GlobalUserListContext';
import { CellRenderers } from 'utils/tableUtils';
import useTierOptions from 'hooks/useTierOptions';

const { Title } = Typography;
const { Option, OptGroup } = Select;

const EnvList = ["Production", "Staging"];
const getEnvironmentInitialValue = () => ({ environments: EnvList.map(e => ({ name: e, branch: 'master' })) });
const getEnvironmentDataArr = (data) => _.map(EnvList, (env) => {
    const envData = _.find(data, (envData) => envData.name === env) || {};
    return (({ 
        name: envData.name, 
        branch: envData.branch, 
        url: _.filter(_.split(envData.url, ",").map(_.trim), v => !_.isEmpty(v)), 
        id: envData.id 
    }));
});

export default forwardRef(function SubproductFormPopup({ editId, activeUser, productId, ...props }, ref) {
    //used useFeatureFlag hook instead of HOC to avoid forwardRef warning for functional components
    const thisRef = useRef();
    const tagsRef = useRef();
    const parentDataSourceRef = useRef();
    const productRef = useRef();
    const [form, setForm] = useState();
    const [selectedLanguages, setSelectedLanguages] = useState();
    const [users, setUsers] = useState();
    const [products, setProducts] = useState();
    const [subproductTypes, setSubproductTypes] = useState();
    const [existingSubproduct, setExistingSubproduct] = useState();
    const [createdSubproduct, setCreatedSubproduct] = useState();
    const [initialValues, setInitialValues] = useState(getEnvironmentInitialValue);
    const [copyData, setCopyData] = useState(getEnvironmentInitialValue);
    const [confidentialityData, setConfidentialityData] = useState({});
    const [confidentialityDataForSelectedProduct, setConfidentialityDataForSelectedProduct] = useState({});
    const [subproductToCopy, setSubproductToCopy] = useState(undefined);
    const [isCopyingExistingSubProduct, setIsCopyingExistingSubProduct] = useState()
    const [categorization, setCategorization] = useState();
    const [slaConfigs, setSlaConfigs] = useState([])
    const [slaID, setSlaID] = useState();
    const [teamsOptions, setTeamOptions] = useState([]);
    const [teamId, setTeamId] = useState();
    const globalSettings = useGlobalSettingsContext();
    const { refreshUsers } = useGlobalUserListContext();
    const [globalFilters] = useState(getGlobalFilter_UNSAFE);
    
    const { loading: productStatusLoading, statusList } =  useFetchProductStatus();
    
    
    const subProduct = getVocabulary("subProduct")

    const [selectedProduct, setSelectedProduct] = useState(productId ? +productId : undefined);
    const [loading, setLoading] = useState(false);
    const validateAPIRef = useRef();
    const featureFlags = useFeatureFlag();
    const {tierBasedTags, allTierBasedTags} = useTierBasedTags();
    const tierOptionsWithCustom = useTierOptions();

    useEffect(() => {
        const { promise: promise1, cancel: cancel1 } = ProductAPI.getAllQuick();
        const { promise: promise3, cancel: cancel3 } = SubProductAPI.getTypes();

        promise1.then(setProducts, _.noop);
        promise3.then(setSubproductTypes, _.noop);

        return () => {
            cancel1();
            cancel3();
        }
    }, []);

    const fetchTeams = useCallback(() => {
        const {promise, cancel} = TeamsAPI.getAllTeams({ teamUser: true });
        const promise2 = promise.then(data => {
            const teams = data.map(d => ({value: d.id, label: d.name}));
            setTeamOptions(teams);
            return teams;
        }, _.noop)
        return { promise: promise2, cancel};
      }, [])

      useEffect(() => {
            const { cancel } = fetchTeams();
            return cancel;
    }, [fetchTeams]);

    useEffect(() => {
        if (form && !existingSubproduct) {
            const type = _.find(subproductTypes, { name: CellRenderers.VALUE_OR_NA() });
            form.setFieldsValue({ status: statusList[0], types: type ? [type.id] : undefined })
        }
    }, [statusList, form, existingSubproduct, subproductTypes])

    const fetchUsers = useCallback(() => {
        setLoading(true);
        const { promise, cancel } = UserAPI.getAll(selectedProduct || existingSubproduct?.product || productId);
        const promise2 = promise.then(data => {
            const userMap = data.map(d => ({ value: d.id, label: d.displayName }));
            setUsers(userMap);
            setLoading(false);
            return userMap;
        }, _.noop).catch(() => {
            setLoading(false);
        });
        return { promise: promise2, cancel };
    }, [productId, selectedProduct, existingSubproduct]);

    useEffect(() => {
        return fetchUsers().cancel;
    }, [fetchUsers]);

    useEffect(() => {
        const { promise, cancel } = GlobalSettingAPI.getSLAList();
        promise.then(setSlaConfigs);
        return cancel;
    }, []);

    useEffect(() => {
        if (editId) {
            const { promise, cancel } = SubProductAPI.get(editId);
            promise.then(updateDataAfterGet).then((data) => {
                setExistingSubproduct(data);
                setSlaID(data.slaTemplateId);
                setTeamId(data.teamDto);
                // on edit set selected languages if any
                if (!(_.isEmpty(data.programmingLanguage))) { 
                    setSelectedLanguages(data.programmingLanguage);
                }
                
                checkAndSetConfidentialityData(data, setConfidentialityData);
            }, _.noop);
            return cancel;
        } else {
            setSlaID(_.find(slaConfigs, { name: 'Default SLA' })?.id);
        }
    }, [editId, slaConfigs]);

    useEffect(() => {
        if (subproductToCopy) {
            setLoading(true);
            const { promise, cancel } = SubProductAPI.get(subproductToCopy);
            promise.then(updateDataAfterGet).then((data) => {
                const dataWithoutProductAndOwners = _.omit(data, ["product", "businessOwner", "complianceOwner", "engineeringOwner", "securityOwner", "supportOwner", "teamDto"])/* {
                    ...data,
                    product: undefined,
                    businessOwner: undefined,
                    complianceOwner: undefined,
                    engineeringOwner: undefined,
                    securityOwner: undefined,
                    supportOwner: undefined,
                } */;
                form.setFieldsValue(dataWithoutProductAndOwners);
                checkAndSetConfidentialityData(data, setConfidentialityData);
                setCopyData({
                    ...dataWithoutProductAndOwners,
                    environments: getEnvironmentDataArr(dataWithoutProductAndOwners.environmentDtos),
                });
                productRef.current = data.product;
                setLoading(false);
            }).catch(() => setLoading(false));
            return cancel;
        }
    }, [subproductToCopy, form]);

    useEffect(() => {
        if (!isCopyingExistingSubProduct) {
            setSubproductToCopy(undefined);
            if (!_.isUndefined(confidentialityDataForSelectedProduct)) {
                setConfidentialityData(confidentialityDataForSelectedProduct);
            }
        }
    }, [isCopyingExistingSubProduct, confidentialityDataForSelectedProduct]);

    useEffect(() => {
        if (form && existingSubproduct) {
            form.setFields([{
                name: "environments",
                value: getEnvironmentDataArr(existingSubproduct.environmentDtos)
            }])
        }
    }, [form, existingSubproduct]);
    useImperativeHandle(ref, () => ({ ...thisRef.current }));


    const onNext = useCallback(() => {
        if(!form || editId){
            return;
        }
        const currentCategorization = form.getFieldValue('tier');
        if(!currentCategorization || currentCategorization === categorization){
            return;
        }
        const { selectedTierGlobalFields,  categorizationConfidentiality} = getDefaultTierValues(currentCategorization, globalSettings.tierCategory);
        const defaultResponses = _.cloneDeep(getKeyValuePair(selectedTierGlobalFields))
        form.setFields(defaultResponses);
        setConfidentialityData(categorizationConfidentiality);
        setCategorization(currentCategorization);
    },[editId,form, categorization, globalSettings.tierCategory]);

    useImperativeHandle(ref, () => ({ ...thisRef.current }));

    const onSuccess = useCallback((data) => {
        setCreatedSubproduct(data);
        if(editId){
            message.success(
                <>Edited {subProduct} - {data.name}</>
            );
        } 
        thisRef.current.close();
    },[editId, subProduct]);
    const onProductChange = useCallback((values) => {
        // ignore tracking product if the subproduct is in editing mode
        if(!editId && values.product && values.product !== selectedProduct){
            setSelectedProduct(values.product);
        }
        if (values.hasOwnProperty('teamDto')) {
            setTeamId(values.teamDto);
        }
        if(values.hasOwnProperty('slaTemplateId')){
            setSlaID( values.slaTemplateId);
        }
    }, [editId, selectedProduct]);

    useEffect(() => {
        // do not change the owners if the subproduct is in editing mode
        if(!editId && selectedProduct && form){
            const { promise, cancel } = ProductAPI.v2.get(selectedProduct);
            promise.then((data) => {
                const values = {};
                const riskFieldNames = getSecurityAndComplianceFields().fields.map(f => f.name).filter(Boolean);
                OWNER_FIELDS.concat(riskFieldNames).forEach(ownerKey => values[ownerKey] = data[ownerKey]);
                const _id = data.teamDto?.id;
                values.teamDto = _id;
                setTeamId(_id);
                if(data.slaTemplateId){
                    values.slaTemplateId = data.slaTemplateId;
                    setSlaID(data.slaTemplateId);
                }
                
                form.setFieldsValue(values);
                setInitialValues(obj => ({...obj, ...values}))

                const _confidentialityData = {};
                if(_.isString(data.confidentialityOptions) && data.confidentialityOptions.length > 0){
                    data.confidentialityOptions.split(",").forEach((key) => {
                        _confidentialityData[key.trim()] = true;
                    });
                }
                // This will be empty if new product selected has no data
                setConfidentialityData(_confidentialityData);
                setConfidentialityDataForSelectedProduct(_confidentialityData);
            })
            return cancel;
        }
    }, [form, selectedProduct, editId])

    const onConfidentialityChange = useCallback((e, id) => {
        const _confidentialityData = {...confidentialityData};
        id = "C"+id;
        if(e.target.checked){
            _confidentialityData[id] = true;
        } else {
            delete _confidentialityData[id];
        }
        setConfidentialityData(_confidentialityData);
    }, [confidentialityData]);

    const modifyData = useCallback((data) => {
        data = updateDataBeforePush({
            data,
            confidentialityData,
            tierCategory: globalSettings.tierCategory,
            sourceSubProductId: subproductToCopy,
            isCopyingExistingSubProduct,
            sourceProductId: productRef.current
        });
        return data;
    }, [confidentialityData, subproductToCopy, isCopyingExistingSubProduct, globalSettings.tierCategory]);

    const securityAndComplianceData = getSecurityAndComplianceFields({
        onConfidentialityChange,
        confidentialityData
    }, "subProduct");

    const clonedSubProducts = useCallback(() => 
        editId ? () => ({ promise: Promise.resolve([]), cancel: _.noop }) : SubProductAPI.getClonable({ productId: selectedProduct }),
    [selectedProduct, editId])

    const clearSourceSubProduct = useCallback(() => {
        if(!editId){
            form.setFields([{
                name: "sourceSubProductId",
                value: undefined
            }, {
                name: "name",
                value: undefined
            }])
        }
    }, [form, editId]);

    const advancedFields = useMemo(() => {
        return [
        {
            name: "environments",
            render: RenderEnvList
        }, {
            name: "programmingLanguage",
            label: "Programming Language",
            type: FieldTypes.TAGS,
            colSpan: 24,
            placeholder: "Select language or type your own",
            options: programmingLanguages,
            props: {
                onChange: setSelectedLanguages
            },
            NoDefaultLengthRule: true,
            rules: [
                validateLanguagesRule
            ]
        },
        {
            name: "technologies",
            label: "Technologies",
            type: FieldTypes.TAGS,
            props: {
                optionsMutator: () => undefined,
                children: getTechnologiesOptions(selectedLanguages)
            },
            colSpan: 24,
        },
        {
            name: "repoLink",
            label: "Repository URL",
            rules:[urlRegexValidator],
            colSpan: 24
        },
        {
            name: "repoType",
            label: "Repository Type",
            type: FieldTypes.SELECT,
            options: REPOSITORY_TYPE_OPTIONS
        }, {
            name: "origin",
            label: "Origin",
            colSpan: 12,
            type: FieldTypes.SELECT,
            options: ORIGIN_OPTIONS
        }]
    }, [selectedLanguages])

    const getSLAFields = useMemo(() => (
        [
            {
                name: "slaTemplateId",
                label: <>SLA Name <Tooltip title={<>
                    1. If SLA is defined on product level, then product-level SLA will be applied.<br />
                    2. If SLA is not defined on product level either, then default (global) SLA will be applied.
                </>}><InfoIcon className="m-l-xs"/></Tooltip></>,
                placeholder: "SLA Name",
                type: FieldTypes.SELECT,
                props: {
                    allowClear: true,
                    defaultValue: slaID,
                },
                options: slaConfigs

            }, {
                name: "slaPreview",
                render: () => <SLAPreview slaID={slaID} viewOnly={!slaID} productID={selectedProduct} />
            }
        ]
    ), [slaConfigs, slaID, selectedProduct])

    const ssdlcFields = [{
        name: "subProductToolsMap",
        type: FieldTypes.LABEL,
        render: (props) => <ToolSelector {...props} existingSubproduct={existingSubproduct} />
    }];

    const warningField = (!_.isEmpty(globalFilters.globalFilterProducts) || !_.isEmpty(globalFilters.globalFilterGroups)) && !editId  ? [{
        name: "warning",
        type: FieldTypes.LABEL,
        render: () => (
            <Alert className='m-b-sm' message = {<>In order to view newly created <Vocabulary name="subProduct" isPlural />, make sure to include them as part of the Global Filters.</>} type="warning" />
        )
    }] : []

    const pages = [{
        name: "Basic Information",
        fields: [...warningField, ...getBasicInfoFields({ form, parentDataSourceRef, products, editId, subproductTypes, existingSubproduct, selectedProduct, clonedSubProducts, setSubproductToCopy, clearSourceSubProduct, setIsCopyingExistingSubProduct, isCopyingExistingSubProduct, subproductToCopy, tagListRef: tagsRef, validateAPIRef, copyData, productStatusLoading, statusList,globalSettings, tierBasedTags, allTierBasedTags, featureFlags, tierOptionsWithCustom, isVersionsEnabled: featureFlags.VERSIONS })]
    }];

    pages.push({
        name: "SLA",
        fields: getSLAFields
    },{
        name: "Risk Assessment",
        fields: featureFlags.RISK_SCORE_3_0
            ? 
            [
                _.find(pages[0].fields, { name: "tier" }),
                {
                  ..._.find(pages[0].fields, { name: "tierTags" }),
                  style: { marginBottom: MARGIN_LG },
                },
                {
                  ..._.find(pages[0].fields, { name: "tags" }),
                  style: { display: "none" },
                },
            ]
            : securityAndComplianceData.fields,
    }, {
        name: "Owners",
        fields: getOwnerFields({ users, activeUser, form, fetchUsers, loading, type: "subProduct", teamsOptions, fetchTeams, teamId, setTeamId, refreshUsers })
    }, {
        name: "Security Tools",
        fields: ssdlcFields,
    }, {
        name: "Advanced",
        fields: advancedFields,
    })

    return (
        <BaseModal
            modalProps={{
                footer: null,
                width: MODAL_SIZE_MEDIUM,
                style: { top: 80 },
                title: <>{!editId ? "Add" : "Edit"} <Vocabulary name="subProduct" /></>
            }}
            ref={thisRef}
            afterClose={() => props.afterClose(createdSubproduct)}
            showExitWarning
        >
            <WizardFormV2
                isEdit={!_.isUndefined(editId)}
                onCancel={thisRef.current?.close}
                pages={pages}
                onChange={onProductChange}
                key={(!editId && isCopyingExistingSubProduct && !_.isUndefined(copyData)) ? `subproduct-copy-form-${copyData.id}` : 'subproduct-form'}
                initialValues={editId ?
                    {
                        ...existingSubproduct,
                    } : 
                    {
                        ...((isCopyingExistingSubProduct && !_.isUndefined(copyData)) ? copyData : initialValues),
                        product: selectedProduct
                    }
                }
                formRef={setForm}
                api={SubProductAPI[!editId ? "save" : "update"]}
                beforeAPI={modifyData}
                onSuccess={onSuccess}
                validator={parseErrorMessage}
                loading={editId && _.isEmpty(existingSubproduct)}
                loadingNext={loading}
                showSkipSaveBtnFromStep={0}
                onNext = {onNext}
            />
        </BaseModal>
    )
});

function parseErrorMessage(e) {
    const message = _.lowerCase(e.response?.data?.message ?? "");
    if (message.startsWith("subproduct with name") || message.startsWith("subproduct name")) {
        return [{
            name: "name",
            errors: [message]
        }]
    }
}

function getBasicInfoFields({ form, tierOptionsWithCustom, products, editId, subproductTypes, existingSubproduct, selectedProduct, clearSourceSubProduct, isCopyingExistingSubProduct, validateAPIRef, copyData, productStatusLoading, statusList, tierBasedTags, allTierBasedTags, featureFlags, isVersionsEnabled }) {
    return [
        {
            name: "product",
            label: <>Parent <Vocabulary name="product" /></>,
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: products,
            loading: _.isUndefined(products),
            disabled: editId,
            props: {
                onChange: clearSourceSubProduct
            },
            rules: [{
                required: true,
                message: <>Select a parent <Vocabulary name="product" isLowerCase={true}/>.</>
            }],
    }
    , {
        name: "name",
        label: <><Vocabulary name="subProduct" /> Name</>,
        colSpan: 12,
        NoDefaultLengthRule: true,
        props: {
            autoFocus: true
        },
        rules: subproductNameFieldRule(appendVersionToName(existingSubproduct), selectedProduct, validateAPIRef, form, isVersionsEnabled),
    },    
    {
        name: "description",
        label: "Description",
        type: FieldTypes.TEXT_AREA,
        NoDefaultLengthRule: true,
        render: () => {
            return (
                <MessageEditor
                    placeholder={'Description'}
                    toolBarIdentifier={'message-editor-sub-product-description'}
                    key={'sub-product-description'}
                    hideEmoji
                    hideOldUpload
                    alwaysOpen
                    showFooterButtons={false}
                />
            );
        },
        rules: [editorRules({required: false, min: 0})]
    }, {
        name: "types",
        label: <><Vocabulary name="subProduct" /> Type</>,
        colSpan: 12,
        type: FieldTypes.MULTI_SELECT,
        options: subproductTypes
    }, {
        name: "status",
        label: "Status",
        type: FieldTypes.SELECT,
        colSpan: 12,
        loading: productStatusLoading,
        options: statusList
    }, {
        name: "versionNumber",
        label: "Version Number",
        help: <>Please enter <Vocabulary name="subProduct" isLowerCase={true} /> version e.g. 1.0 or 0.1.23</>,
        colSpan: 12,
        NoDefaultLengthRule: true,
        rules: [{
            max: 128,
            message: "Maximum length is 128 characters"
        }]
    }, 
    {
        name: 'tier',
        label: 'Tier',
        type: FieldTypes.SELECT,
        colSpan: 12,
        options:  featureFlags.RISK_TIERS ? tierOptionsWithCustom : featureFlags.RISK_SCORE_3_0 ? TIER_OPTIONS_WITH_CUSTOM : TIER_OPTIONS,
        props: {
            onChange: (tier) => {
                if(featureFlags.RISK_SCORE_3_0){
                    const tierTags = featureFlags.RISK_TIERS ? _.find(tierBasedTags, { id: tier })?.tags : tierBasedTags[tier];
                    syncTagsFromTier({
                        oldTags: form.getFieldValue("tierTags"),
                        newTier: tier,
                        tierBasedTags,
                        tags: form.getFieldValue("tags"),
                        form,
                        otherValues: { tierTags },
                        isNewCustomTiers: featureFlags.RISK_TIERS,
                    });
                }
            }
        }
    }, featureFlags.RISK_SCORE_3_0 ? {
        name: "tierTags",
        render: () => (
            <TierBasedTagField
                allTags={allTierBasedTags}
                tier={form?.getFieldValue("tier") || CUSTOM_TIER}
                tierBasedTags={tierBasedTags}
                onChange={(tags, { deleted, added }) => {
                    syncTagsFromTier({
                        oldTags: deleted,
                        newTags: added,
                        tierBasedTags,
                        tags: form.getFieldValue("tags"),
                        form,
                        otherValues: { tier: CUSTOM_TIER },
                        isNewCustomTiers: featureFlags.RISK_TIERS,
                    });
                }}
            />
        ),
        style: { display: "none" },
    } : undefined,
    {
        name: "tags",
        render: () => {
            const source = isCopyingExistingSubProduct ? copyData : existingSubproduct
            return <TagsField staticTags={source?.tagsMetaMap?.PRODUCT} onChange={(tags, { deleted }) => {
                if(featureFlags.RISK_SCORE_3_0){
                    syncTierFromTags({ deletedTag: deleted, form });
                }
            }} />
        }
    },
    {
        name: "artifacts",
        render: () => {
            return <ArtifactsField form={form} />
        }
    }].filter(f => !_.isEmpty(f));
}

export function RenderEnvList() {
    return (
        <Alert
            description={
                <Form.List name="environments">
                    {(fields) => (
                        <>
                            {
                                fields.map((field, index) => {
                                    return (
                                        <Row key={`field-${index}`} gutter={BASE_GUTTER}>
                                            <Col span={24}>
                                            {
                                            field.key === 0 ?
                                                <Title level={5}><EnvironmentName name="Production" /> Environment</Title>
                                            :
                                                <Title level={5}><EnvironmentName name="Staging" /> Environment</Title>
                                            }
                                            </Col>
                                            <Col span={24}>
                                                <Form.Item
                                                    {...field}
                                                    label={<span className="text-bold">Deployed URL</span>}
                                                    name={[field.name, 'url']}
                                                    fieldKey={[field.fieldKey, 'url']}
                                                    rules={[multiUrlValidator()]}
                                                >
                                                    <Select mode='tags' placeholder="Deployed URL" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )
                                })
                            }
                        </>
                    )}
                </Form.List>
            }
            type="info"
        />

    )
}

function validateLanguagesRule() {
    return {
        validator: languageValidator,
    }
}
function languageValidator(_, values) {
    if (values?.length) {
        for (const value of values) {
            if (!isNaN(value)) {
                return Promise.reject("Entered input does not look like a valid language.");
            }
            else if(value.length>25){
                return Promise.reject("Maximum length is 25 characters.")
            }
        }
    }
    return Promise.resolve();
}

export const getTechnologiesOptions = (languages) => _.map(languages, language => {
        return <OptGroup label={language}>
            {_.map(technologiesMap[language], tech => <Option value={tech}>{tech}</Option>)}
        </OptGroup>
    });
export const getTechnologiesOptionsV2 = (languages) =>
  _.map(languages, (language) => {
    return {
      label: language,
      options: _.map(technologiesMap[language], (tech) => ({
        label: tech,
        value: tech,
      })),
    };
  });

const technologiesMap = {
    "C++": ['Qt','JUCE'],
    "Java": ['Spring','Play','Hibernate','Dropwizard','Struts','Blade','Vaadin','Javaserver faces ','Google web toolkit','Grails','Java EE', 'Spring Boot', 'Micronaut'],
    "PHP": ['Laravel','CodeIgniter','Symfony','CakePHP','Yii','Zend Framework','Phalcon','FuelPHP','PHPixie','Slim'],
    "Python": ['Bottle','CherryPy','AIOHTTP','Dash','Falcon','Flask','Giotto','Django','Growler','Uvloop','Sanic','MorePath','Picnic','Pylons Framework','Pyramid','TurboGears','Web2py','Hug','CubicWeb'],
    "Go": ['Martini','Gin Gonic','Buffalo','Goji','Gorilla','Revel','Web.go','Gocraft','Mango','Net/HTTP'],
    "JavaScript": ['Angular','Vue JS','Next JS','React JS','Ember JS','Svelte JS','Gatsby JS','Nuxt JS','Bootstrap JS','Node JS','Express JS','Mocha JS','Jasmine','Jest'],
    "ErLang": ['Cowboy','n2o','MochiWeb','Nitrogen']
};